import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { tabId } from "../components/SegmentedTabs";
import { pageTabFromHash, pageTabParam, resolvePageTab, withPageTab } from "./pageModel";
import type { SectionView } from "./pageModel";

/**
 * Which product page's tab shows (2+ pages) — the section's tab rules
 * (ProductPagesSection.tsx, "Tabs"): the reader's pick, kept in the URL's
 * hash; a #pdpUrlId the reader navigates to; a page just added; the shown
 * tab scrolled into view along its row. `shownId` is what shows
 * (`resolvePageTab`); `setPicked` is the section's for a tab click and a
 * delete.
 *
 * The section calls it after its count effect, where this code sat, so the
 * effects run in the same order.
 */

/** SegmentedTabs' idPrefix: tabs are `product-page-tab-<id>` (tabId), panels `product-page-panel-<id>`. */
export const TAB_PREFIX = "product-page";

interface Options {
  productId: number;
  /** The listed pages' ids, oldest first ([] until the list lands). */
  ids: ReadonlyArray<number>;
  /** The list has landed (`rows != null`): until then the URL is left alone. */
  listed: boolean;
  /** Tabs show: 2+ pages. */
  tabs: boolean;
  /** `sectionView`: the tab row is drawn only in the card. */
  view: SectionView;
  /** useProductPages' `lastAddedId`: the page an add created, once listed. */
  lastAddedId: number | null;
  /** The section's `className` — the page's `hidden` while a video is open. */
  className?: string;
}

export const usePageTabPick = ({ productId, ids, listed, tabs, view, lastAddedId, className }: Options) => {
  // The tab the reader picked (or the URL named); `shownId` is what shows.
  const [picked, setPicked] = useState<number | null>(() => pageTabFromHash(window.location.hash));
  const shownId = resolvePageTab(ids, picked);

  // ---- the pick in the URL ----
  // undefined = nothing listed yet: leave the URL alone (a refresh keeps the
  // hash until the list says whether that page is still here).
  const urlTab = listed ? pageTabParam(ids, shownId) : undefined;
  const location = useLocation();
  useEffect(() => {
    if (urlTab === undefined) return;
    const here = window.location;
    // This product's URL only: a render left over from leaving must not put
    // the hash on the list's URL.
    if (new URLSearchParams(here.search).get("productId") !== String(productId)) return;
    const next = withPageTab(here, urlTab);
    if (next != null) window.history.replaceState(window.history.state, "", next);
  }, [urlTab, location, productId]);

  // A #pdpUrlId the reader navigates to on this page (a link pasted over this
  // one, an edited hash) is a pick: nothing remounts, so the initial read
  // above never sees it, and the effect above would write the old pick back
  // over it. hashchange never fires for that effect's own replaceState. Read
  // from the event, since that effect may already have rewritten the hash.
  useEffect(() => {
    const onHash = (e: HashChangeEvent) => {
      const id = pageTabFromHash(new URL(e.newURL).hash);
      if (id != null) setPicked(id);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // The shown tab in view along its row: a link to the last page at 375
  // would open with it scrolled off the right edge (the row scrolls
  // sideways, SegmentedTabs). The row alone scrolls — never the page, as
  // scrollIntoView would when the section is below the fold. Keyed on the
  // view too: the hook lands the rows a render before it clears `loading`,
  // so the tabs first draw a render after `tabs` turns true; and on
  // `className`, the page's `hidden` while a video is open, whose close
  // draws the row again from scrollLeft 0.
  const count = ids.length;
  useLayoutEffect(() => {
    if (view !== "card" || !tabs || shownId == null) return;
    const tab = document.getElementById(tabId(TAB_PREFIX, String(shownId)));
    const row = tab?.parentElement;
    if (!tab || !row || row.scrollWidth <= row.clientWidth) return;
    const r = row.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (t.left < r.left) row.scrollLeft -= r.left - t.left + 3;
    else if (t.right > r.right) row.scrollLeft += t.right - r.right + 3;
  }, [view, tabs, shownId, count, className]);

  // ---- an add opens its page ----
  // Set once the list with the new page has landed; if that re-read failed
  // the page isn't listed yet and the first shows until it is.
  const seenAdded = useRef(lastAddedId);
  useEffect(() => {
    if (lastAddedId == null || lastAddedId === seenAdded.current) return;
    seenAdded.current = lastAddedId;
    setPicked(lastAddedId);
  }, [lastAddedId]);

  return { picked, setPicked, shownId };
};
